<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { CheckCircle, AlertCircle, XCircle, Clock, CreditCard } from 'lucide-vue-next'

interface Subscription {
  id: number
  status: string
  student_name: string
  tier_name: string
  amount: number
  session_day: string | null
  session_time: string | null
}

interface Props {
  status: 'accepted' | 'rejected' | 'already_processed' | 'payment_refused' | 'link_expired' | 'error'
  subscription?: Subscription
  message?: string
}

const props = defineProps<Props>()

function goToDashboard() {
  router.visit('/teacher/dashboard')
}
</script>

<template>
  <AppLayout>
    <main class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div class="max-w-md w-full">

        <!-- Accepted State -->
        <div v-if="status === 'accepted'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <CheckCircle class="w-12 h-12 text-green-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Student Accepted!</h1>
          <p class="text-gray-600 mb-6">
            You have accepted {{ subscription?.student_name }}'s subscription. The first payment has been successfully captured.
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <!-- Rejected State -->
        <div v-else-if="status === 'rejected'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <XCircle class="w-12 h-12 text-red-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Student Rejected</h1>
          <p class="text-gray-600 mb-6">
            You have declined the subscription request from {{ subscription?.student_name }}. The payment authorization has been cancelled.
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <!-- Already Processed State -->
        <div v-else-if="status === 'already_processed'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <Clock class="w-12 h-12 text-gray-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Already Processed</h1>
          <p v-if="subscription?.status === 'cancelled'" class="text-gray-600 mb-6">
            This subscription request was cancelled by the student before it could be processed.
          </p>
          <p v-else-if="subscription?.status === 'rejected'" class="text-gray-600 mb-6">
            This subscription request has already been declined (or automatically expired).
          </p>
          <p v-else-if="subscription?.status === 'active'" class="text-gray-600 mb-6">
            This subscription request has already been accepted and is active.
          </p>
          <p v-else class="text-gray-600 mb-6">
            This subscription request has already been processed (Status: {{ subscription?.status }}).
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <!-- Link Expired: the confirmation window closed before they clicked -->
        <div v-else-if="status === 'link_expired'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock class="w-12 h-12 text-gray-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">This request has expired</h1>
          <p class="text-gray-600 mb-6">
            The confirmation window for this request has closed, so it was cancelled automatically and the
            student was not charged. If they are still interested, they can send you a new request.
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <!-- Payment Refused: the tutor said yes, the student's bank said no -->
        <div v-else-if="status === 'payment_refused'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard class="w-12 h-12 text-amber-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Payment refused</h1>
          <p class="text-gray-600 mb-2">
            You accepted {{ subscription?.student_name }}, but their bank refused the payment. This is nothing you did wrong.
          </p>
          <p class="text-gray-600 mb-6">
            We have asked them to try again with another card, and your slot is free in the meantime. Our team has been notified.
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <!-- Error State -->
        <div v-else-if="status === 'error'" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle class="w-12 h-12 text-red-600" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">An error occurred</h1>
          <p class="text-gray-600 mb-6">
            {{ message || 'Could not process the action.' }}
          </p>
          <button
            @click="goToDashboard"
            class="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Go to Dashboard
          </button>
        </div>

      </div>
    </main>
  </AppLayout>
</template>
